import { useState } from "react";
import toast from "react-hot-toast";

import { Helmet } from "react-helmet";
import SectionHeader from "../SectionHeader";
import Modal from "../Modal";
import PartnerCompanyForm from "./PartnerCompanyForm";
import DataTable from "react-data-table-component";
import TableLoader from "../TableLoader";

import usePartnerCompanies from "../../hooks/usePartnerCompanies";

import partnerCompanyService from "../../services/partnerCompanyService";
import PartnerCompanyView from "./PartnerCompanyView";

const DesignAndEngineeringPartners = () => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const [selectedCompany, setSelectedCompany] = useState(null);

  const [isCompanyViewOpen, setIsCompanyViewOpen] = useState(false);
  const [isCompanyFormOpen, setIsCompanyFormOpen] = useState(false);

  const [isSubmittingCompany, setIsSubmittingCompany] = useState(false);

  const {
    data: partnerCompaniesData,
    isLoading: isLoadingPartnerCompanies,
    mutate: mutatePartnerCompanies
  } = usePartnerCompanies({
    page,
    perPage,
    sortField,
    sortOrder
  });

  const partnerCompanies = partnerCompaniesData?.partnerCompanies || [];
  const partnerCompanyCount = partnerCompaniesData?.count || 0;

  const handleCloseCompanyForm = () => {
    setSelectedCompany(null);
    setIsCompanyFormOpen(false);
  }

  const handleCloseCompanyView = () => {
    setSelectedCompany(null);
    setIsCompanyViewOpen(false);
  }

  const handleCompanyFormSubmit = async (payload) => {
    try {
      if (selectedCompany) {
        setIsSubmittingCompany(true);

        const { partnerCompany } = await partnerCompanyService.updatePartnerCompany({
          partnerCompanyId: selectedCompany._id,
          updateData: payload
        });

        await mutatePartnerCompanies();

        setIsSubmittingCompany(false);
        setIsCompanyFormOpen(false);
        setSelectedCompany(null);

        toast.success(`Partner ${partnerCompany.name} updated`);
      } else {
        setIsSubmittingCompany(true);

        const { partnerCompany } = await partnerCompanyService.createPartnerCompany(payload);

        await mutatePartnerCompanies();

        setIsSubmittingCompany(false);
        setIsCompanyFormOpen(false);

        toast.success(`Partner ${partnerCompany.name} created`);
      }
    } catch (error) {
      console.log(error);
      toast.error(error?.message || "Something went wrong.");
      setIsSubmittingCompany(false);
    }
  }

  const handleCompanyClicked = (company) => {
    setSelectedCompany(company);
    setIsCompanyViewOpen(true);
  }

  const handleUpdateCompanyClicked = (company) => {
    setSelectedCompany(company);
    setIsCompanyFormOpen(true);
  }

  const handlePageChange = page => {
    setPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const handleSort = async (column, sortDirection) => {
    setSortField(column.sortField);
    setSortOrder(sortDirection === 'desc' ? 1 : -1);
  };

  const columns = [
    {
      name: 'Name',
      selector: row => row.name,
      sortable: true,
      sortField: 'name'
    },
    {
      name: 'Email',
      selector: row => row.email,
      sortable: true,
      sortField: 'email'
    },
    {
      name: 'Admin',
      selector: row => row?.admin?.fullName || 'Not invited',
      sortable: true,
      sortField: 'admin'
    },
    {
      name: 'Staff',
      selector: row => row.staff.length,
    },
    {
      name: '',
      right: "true",
      cell: row => (
        <div className='flex items-center gap-2'>
          <button
            onClick={() => handleUpdateCompanyClicked(row)}
            className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-ase-blue text-white text-nowrap'
          >
            Update
          </button>
        </div>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>D & E Partners | ASE Group</title>
      </Helmet>

      <div className='border border-ase-blue rounded-lg pb-1'>
        <SectionHeader
          title='Partners'
          actionsLeft={
            <button
              onClick={() => setIsCompanyFormOpen(true)}
              className='px-2 py-1 bg-white rounded-md text-ase-blue font-semibold text-xs'
            >
              Add
            </button>
          }
        />

        <DataTable
          columns={columns}
          data={partnerCompanies}
          striped
          highlightOnHover
          pointerOnHover
          onRowClicked={handleCompanyClicked}
          progressPending={isLoadingPartnerCompanies}
          progressComponent={<TableLoader />}
          sortServer
          onSort={handleSort}
          pagination
          paginationServer
          paginationTotalRows={partnerCompanyCount}
          paginationRowsPerPageOptions={[10, 20, 50]}
          paginationPerPage={perPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </div>

      <Modal isOpen={isCompanyFormOpen} overflowType='visible'>
        <div className="bg-white rounded-lg border border-ase-blue w-full md:w-auto md:min-w-[600px] pb-1 overflow-visible">
          <div className="flex items-center justify-between text-lg bg-ase-blue text-white p-4 font-bold rounded-t-md">
            <p>{selectedCompany ? 'Update' : 'Add'} company</p>

            <p
              onClick={handleCloseCompanyForm}
              className="font-bold cursor-pointer text-2xl"
            >
              X
            </p>
          </div>

          <div className="flex items-center gap-6 px-4 py-4">
            <PartnerCompanyForm
              company={selectedCompany}
              isLoading={isSubmittingCompany}
              onSubmit={handleCompanyFormSubmit}
              onCloseClicked={handleCloseCompanyForm}
            />
          </div>
        </div>
      </Modal>

      <Modal isOpen={isCompanyViewOpen} >
        <PartnerCompanyView
          company={selectedCompany}
          mutateCompanies={mutatePartnerCompanies}
          onCloseClicked={handleCloseCompanyView}
        />
      </Modal>
    </>
  )
}

export default DesignAndEngineeringPartners;
